/**
 * Pattern: Holy Grail (Linda Raschke)
 *
 * Raschke's mechanical pullback entry:
 *   1. ADX(14) > 30 at the entry bar (trending regime — gates the setup)
 *   2. Price pulled back to (within Y ticks of) the 20 EMA
 *   3. Prior bar was a counter-trend pullback bar — the "reset" before
 *      continuation
 *
 * The pattern is a STRICT pullback-into-trend entry. Misses chasing
 * entries (would fail #2: price is too far from EMA at entry) and
 * misses entries during chop (would fail #1: low ADX).
 *
 * Reuses ADX from trendContextScore when available, otherwise computes
 * fresh — same cost either way for one bar.
 *
 * Pure function. No side effects.
 */

import { computeEMA } from '../emas.js';
import { computeADX, adxAtTime } from '../adx.js';

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

const simpleATR = (bars, endIdx, period = 14) => {
  if (endIdx < period) return null;
  let sum = 0;
  let count = 0;
  for (let i = endIdx - period + 1; i <= endIdx; i++) {
    const cur = bars[i];
    const prev = bars[i - 1];
    if (!cur || !prev) continue;
    const hi = Number(cur.high);
    const lo = Number(cur.low);
    const pc = Number(prev.close);
    if (![hi, lo, pc].every(Number.isFinite)) continue;
    sum += Math.max(hi - lo, Math.abs(hi - pc), Math.abs(lo - pc));
    count++;
  }
  return count > 0 ? sum / count : null;
};

export const HOLY_GRAIL_MIN_ADX = 30;
export const HOLY_GRAIL_MAX_DISTANCE_ATR = 0.5;  // entry within 0.5x ATR of 20EMA

export const detectHolyGrail = ({ bars, entryTs, entryPrice, direction, options = {}, adxAtEntry } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs) || !Number.isFinite(entryPrice)) return null;

  const minAdx = options.minAdx || HOLY_GRAIL_MIN_ADX;
  const maxDistAtr = options.maxDistanceAtr || HOLY_GRAIL_MAX_DISTANCE_ATR;
  const trendPeriod = options.trendEma || 20;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  if (entryIdx < trendPeriod) return null;

  // 1. ADX gate — reuse provided value if caller supplied it (from
  // trendContextScore.adxAtEntry), else compute fresh.
  let adx = Number.isFinite(adxAtEntry) ? adxAtEntry : null;
  if (adx == null) {
    try {
      const adxSeries = computeADX(bars, 14);
      adx = adxAtTime(adxSeries, entryTs);
    } catch { /* non-fatal */ }
  }
  if (!Number.isFinite(adx) || adx <= minAdx) {
    return null;  // not trending — Holy Grail doesn't apply
  }

  // 2. Distance from 20 EMA
  const emaSeries = computeEMA(bars, trendPeriod);
  const ema = emaSeries[entryIdx]?.value;
  if (!Number.isFinite(ema)) return null;

  const atr = simpleATR(bars, entryIdx);
  if (!Number.isFinite(atr) || atr <= 0) return null;

  const distance = Math.abs(entryPrice - ema);
  const distanceAtr = distance / atr;
  if (distanceAtr > maxDistAtr) {
    return null;  // entry too far from EMA — not a pullback-into-trend
  }

  // 3. Prior bar was counter-trend pullback (a "reset" bar against the
  // trade direction). For long: prior bar's low < bar before that
  // (price dipped before rising). For short: prior bar's high > bar
  // before that (price popped before falling).
  if (entryIdx < 2) return null;
  const priorBar = bars[entryIdx - 1];
  const beforePrior = bars[entryIdx - 2];
  let counterTrendPullback = false;
  if (dir === 'long') {
    counterTrendPullback = Number(priorBar?.low) < Number(beforePrior?.low);
  } else {
    counterTrendPullback = Number(priorBar?.high) > Number(beforePrior?.high);
  }
  if (!counterTrendPullback) return null;

  return {
    type: 'holy-grail',
    score: 1,  // Holy Grail is binary — either all three checks pass or it doesn't fire
    maxScore: 1,
    aligned: true,
    details: `ADX ${adx.toFixed(1)} · entry ${distanceAtr.toFixed(2)}× ATR from 20EMA · prior bar was counter-trend pullback`,
    evidence: { adx, ema, entryPrice, distance, distanceAtr, atr },
  };
};
